import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { User, Lock, Mail, Phone, LogOut, Check, X } from "lucide-react";
import WelcomeCard from "../../components/common/WelcomeCard";

const AdminProfile = () => {
  const [admin, setAdmin] = useState(null);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("adminToken");
      if (!token) return navigate("/login");

      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/profile`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAdmin(data);
      } catch (err) {
        localStorage.removeItem("adminToken");
        navigate("/login");
      }
    };
    fetchProfile();
  }, [navigate]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage("");

    try {
      const token = localStorage.getItem("adminToken");
      const { data } = await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/updatepassword`,
        { oldPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage("✅ " + data.message);
      setTimeout(() => {
        localStorage.removeItem("adminToken");
        navigate("/login");
      }, 2000);
    } catch (err) {
      setMessage(err.response?.data?.message || "❌ Failed to update password");
    }
  };

  if (!admin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <WelcomeCard />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full mx-auto">
          {/* Header Section */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full mb-4 shadow-lg">
              <User className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Admin Profile</h1>
            <p className="text-gray-600">Manage your account settings and security</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Profile Information Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <User className="w-6 h-6 text-blue-600" />
                Profile Information
              </h2>

              <div className="space-y-5">
                <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-xl transition hover:bg-blue-100">
                  <div className="flex-shrink-0 w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Full Name</p>
                    <p className="text-lg text-gray-800 font-semibold">{admin.name}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-indigo-50 rounded-xl transition hover:bg-indigo-100">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Email Address</p>
                    <p className="text-lg text-gray-800 font-semibold break-all">{admin.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-xl transition hover:bg-purple-100">
                  <div className="flex-shrink-0 w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Phone Number</p>
                    <p className="text-lg text-gray-800 font-semibold">{admin.phoneNo}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Password Change Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Lock className="w-6 h-6 text-blue-600" />
                Change Password
              </h2>

              {message && (
                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${message.includes("✅")
                    ? "bg-green-50 border border-green-200"
                    : "bg-red-50 border border-red-200"
                  }`}>
                  {message.includes("✅") ? (
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <X className="w-5 h-5 text-red-600 flex-shrink-0" />
                  )}
                  <p className={message.includes("✅") ? "text-green-700" : "text-red-700"}>
                    {message.replace("✅ ", "").replace("❌ ", "")}
                  </p>
                </div>
              )}

              <form onSubmit={handlePasswordChange} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      placeholder="Enter current password"
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition outline-none"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition outline-none"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition duration-200 flex items-center justify-center gap-2"
                >
                  <Lock className="w-5 h-5" />
                  Update Password
                </button>
              </form>
            </div>
          </div>

          {/* Logout Button */}
          <div className="mt-6">
            <button
              onClick={() => {
                localStorage.removeItem("adminToken");
                navigate("/login");
              }}
              className="w-full max-w-md mx-auto block bg-gradient-to-r from-red-500 to-red-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition duration-200 flex items-center justify-center gap-2"
            >
              <LogOut className="w-5 h-5" />
              Logout from Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import WelcomeCard from "../../components/common/WelcomeCard";

// const AdminProfile = () => {
//   const [admin, setAdmin] = useState(null);
//   const [oldPassword, setOldPassword] = useState("");
//   const [newPassword, setNewPassword] = useState("");
//   const [message, setMessage] = useState("");
//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchProfile = async () => {
//       const token = localStorage.getItem("adminToken");
//       if (!token) return navigate("/login");

//       try {
//         const { data } = await axios.get(
//           `${process.env.REACT_APP_API_URL}/admin/profile`,
//           { headers: { Authorization: `Bearer ${token}` } }
//         );
//         setAdmin(data);
//       } catch (err) {
//         localStorage.removeItem("adminToken");
//         navigate("/login");
//       }
//     };
//     fetchProfile();
//   }, [navigate]);

//   const handlePasswordChange = async (e) => {
//     e.preventDefault();
//     setMessage("");

//     try {
//       const token = localStorage.getItem("adminToken");
//       const { data } = await axios.put(
//         `${process.env.REACT_APP_API_URL}/admin/updatepassword`,
//         { oldPassword, newPassword },
//         { headers: { Authorization: `Bearer ${token}` } }
//       );

//       setMessage("✅ " + data.message);
//       // Log out after password change
//       setTimeout(() => {
//         localStorage.removeItem("adminToken");
//         navigate("/login");
//       }, 2000);
//     } catch (err) {
//       setMessage(err.response?.data?.message || "❌ Failed to update password");
//     }
//   };

//   if (!admin) return <p className="text-center mt-20">Loading...</p>;

//   return (
//     <div className="p-4">
//      <WelcomeCard />
//     <div className="w-full mx-auto p-6 border rounded shadow">
//       <h2 className="text-2xl font-bold mb-4 text-center">Admin Profile</h2>

//       {/* Profile Data */}
//       <div className="mb-6">
//         <p><strong>Name:</strong> {admin.name}</p>
//         <p><strong>Email:</strong> {admin.email}</p>
//         <p><strong>Phone:</strong> {admin.phoneNo}</p>
//       </div>

//       {/* Password Change */}
//       <h3 className="text-xl font-semibold mb-2">Change Password</h3>
//       {message && (
//         <p className={`mb-2 ${message.includes("✅") ? "text-green-600" : "text-red-600"}`}>
//           {message}
//         </p>
//       )}
//       <form onSubmit={handlePasswordChange} className="flex flex-col gap-3">
//         <input
//           type="password"
//           placeholder="Old Password"
//           value={oldPassword}
//           onChange={(e) => setOldPassword(e.target.value)}
//           className="p-2 border rounded"
//           required
//         />
//         <input
//           type="password"
//           placeholder="New Password"
//           value={newPassword}
//           onChange={(e) => setNewPassword(e.target.value)}
//           className="p-2 border rounded"
//           required
//         />
//         <button
//           type="submit"
//           className="bg-blue-600 text-white p-2 rounded hover:bg-blue-700 transition"
//         >
//           Update Password
//         </button>
//       </form>

//       {/* Logout */}
//       <button
//         onClick={() => {
//           localStorage.removeItem("adminToken");
//           navigate("/admin/login");
//         }}
//         className="mt-4 bg-red-600 text-white p-2 rounded w-full hover:bg-red-700 transition"
//       >
//         Logout
//       </button>
//     </div>
//     </div>
//   );
// };

// export default AdminProfile;
